import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ClerkFailed } from '@clerk/react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CHUNK_ERROR, CLERK_RETRY_FLAG, FORM_TIMEOUT_MS, hostedClerkUrl } from '@/lib/clerkConfig';

/**
 * Keeps a failed Clerk load from silently losing a sign-up (2026-10-12).
 *
 * Clerk's sign-in / sign-up UI arrives as separate script chunks from the Clerk frontend API
 * (clerk.<domain>/npm/@clerk/clerk-js@… and, since Core 3, @clerk/ui@…). When one of them doesn't load in a visitor's browser
 * (a dropped request, an ad/privacy blocker or web filter, a CDN miss), the page used to show
 * our heading and an empty space — no form, no message — and the visitor left. Seen in Microsoft
 * Clarity on app.gizmodata.com's sign-up page ("failed chunk load from an external script").
 *
 * The guard shows a way forward instead: when clerk-js fails outright (<ClerkFailed>), when a
 * chunk-load error surfaces, or when the form still isn't there after FORM_TIMEOUT_MS, it renders
 * a short explanation, **Try again** (a fresh reload — the chunks are immutable, so a retry
 * refetches only what failed) and a link to Clerk's hosted page for the same flow. If the form
 * turns up late after all (a slow connection), the notice goes away again. Each failure is tagged
 * in Clarity (`clerk_form_load_failed`) so these sessions can be filtered and counted.
 *
 * That includes Clerk's own "failed to start" state (<ClerkFailed>, status `error`), which shows the notice
 * straight away. It wasn't tagged before 2026-10-13, so those sessions couldn't be counted; now its tag
 * carries the last error text seen. Pair it with the `human_input` tag (lib/clarity.ts) to separate people
 * from automated browsers.
 */
function tagInClarity(reason: string) {
  const clarity = (window as unknown as { clarity?: (...args: unknown[]) => void }).clarity;
  try {
    clarity?.('set', 'clerk_form_load_failed', reason.slice(0, 120));
    clarity?.('event', 'clerk_form_load_failed');
  } catch { /* analytics must never break the page */ }
}

/** Rendered only inside <ClerkFailed>: reports Clerk's own load failure when it shows. */
const ClerkFailedReport: React.FC<{ onShown: () => void }> = ({ onShown }) => {
  useEffect(() => { onShown(); }, [onShown]);
  return null;
};

const FailureNotice: React.FC<{ mode: 'sign-in' | 'sign-up'; publishableKey?: string }> = ({ mode, publishableKey }) => {
  const hosted = hostedClerkUrl(publishableKey, mode);
  const what = mode === 'sign-up' ? 'sign-up' : 'sign-in';
  return (
    <div role="alert" data-testid="clerk-form-failed"
         className="w-full max-w-sm space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="space-y-1">
          <p className="font-medium">The {what} form didn't load.</p>
          <p>
            This is usually a brief network problem, or a browser extension or network filter blocking part of the page.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" size="sm" onClick={() => window.location.reload()}>
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Try again
        </Button>
        {hosted && (
          <a href={hosted} className="font-medium underline underline-offset-4">
            Use the hosted {what} page instead
          </a>
        )}
      </div>
    </div>
  );
};

/** Wraps Clerk's <SignIn> / <SignUp> and shows a way forward when the form can't load. */
export const ClerkFormGuard: React.FC<{
  mode: 'sign-in' | 'sign-up';
  publishableKey?: string;
  timeoutMs?: number;
  children: React.ReactNode;
}> = ({ mode, publishableKey, timeoutMs = FORM_TIMEOUT_MS, children }) => {
  const container = useRef<HTMLDivElement>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const tagged = useRef(false);
  const lastError = useRef<string | null>(null);
  // Clerk's own failure (<ClerkFailed>): tag it with the last error seen, once per page like every other reason.
  const reportClerkFailed = useCallback(() => {
    if (tagged.current) return;
    tagged.current = true;
    const reason = `clerk-failed: ${lastError.current ?? 'no error text'}`;
    tagInClarity(reason);
    console.warn(`[sign-${mode === 'sign-up' ? 'up' : 'in'}] Clerk failed to start: ${reason}`);
  }, [mode]);

  useEffect(() => {
    const formRendered = () => !!container.current?.querySelector('.cl-rootBox, .cl-card, .cl-signIn-root, .cl-signUp-root');
    const fail = (reason: string) => {
      if (formRendered()) return;
      // A chunk that failed (usually a timeout on a slow link) mostly loads on a second try: reload
      // once by ourselves before asking the visitor. Once per tab, so a blocked chunk can't loop.
      if (reason.startsWith('chunk')) {
        let retried = true;
        try { retried = sessionStorage.getItem(CLERK_RETRY_FLAG) === '1'; } catch { /* storage blocked */ }
        if (!retried) {
          try { sessionStorage.setItem(CLERK_RETRY_FLAG, '1'); } catch { /* storage blocked */ }
          tagInClarity(`auto-retry after ${reason}`);
          window.location.reload();
          return;
        }
      }
      setFailure((current) => current ?? reason);
    };
    const onError = (e: ErrorEvent) => {
      const text = `${e.message ?? ''} ${e.filename ?? ''} ${(e.error as Error | undefined)?.name ?? ''}`;
      lastError.current = text.trim().slice(0, 100);
      if (CHUNK_ERROR.test(text)) fail(`chunk: ${text.trim()}`);
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason as { name?: string; message?: string } | string | undefined;
      const text = typeof reason === 'string' ? reason : `${reason?.name ?? ''} ${reason?.message ?? ''}`;
      lastError.current = text.trim().slice(0, 100);
      if (CHUNK_ERROR.test(text)) fail(`chunk: ${text.trim()}`);
    };
    // A Clerk script/chunk <script> that fails to load (blocked, dropped, 404) — resource errors
    // don't bubble, so listen in the capture phase.
    const onResourceError = (e: Event) => {
      const el = e.target as HTMLScriptElement | null;
      if (el?.tagName !== 'SCRIPT' || !/\/npm\/@clerk\/(clerk-js|ui)@/.test(el.src)) return;
      lastError.current = `script failed ${el.src.replace(/^https?:\/\/[^/]+/, '')}`.slice(0, 100);
      fail(`chunk: script failed ${el.src}`);
    };
    // Clerk catches some chunk failures itself (a timed-out chunk) and only logs them.
    const originalConsoleError = console.error;
    console.error = (...args: unknown[]) => {
      try {
        const text = args.map((a) => (a instanceof Error ? `${a.name} ${a.message}` : String(a))).join(' ');
        lastError.current = text.slice(0, 100);
        if (CHUNK_ERROR.test(text)) fail(`chunk: ${text.slice(0, 200)}`);
      } catch { /* never break logging */ }
      originalConsoleError.apply(console, args as []);
    };
    window.addEventListener('error', onError);
    window.addEventListener('error', onResourceError, true);
    window.addEventListener('unhandledrejection', onRejection);
    const timer = window.setTimeout(() => fail('timeout: form not rendered'), timeoutMs);
    // A slow connection may still deliver the form: clear the notice once it appears.
    const observer = new MutationObserver(() => {
      if (!formRendered()) return;
      setFailure(null);
      try { sessionStorage.removeItem(CLERK_RETRY_FLAG); } catch { /* storage blocked */ }
    });
    if (container.current) observer.observe(container.current, { childList: true, subtree: true });
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('error', onResourceError, true);
      window.removeEventListener('unhandledrejection', onRejection);
      console.error = originalConsoleError;
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [timeoutMs]);

  useEffect(() => {
    if (failure && !tagged.current) {
      tagged.current = true;
      tagInClarity(failure);
      console.warn(`[sign-${mode === 'sign-up' ? 'up' : 'in'}] Clerk form failed to load: ${failure}`);
    }
  }, [failure, mode]);

  return (
    <div className="flex w-full flex-col items-center gap-4">
      {failure
        ? <FailureNotice mode={mode} publishableKey={publishableKey} />
        : (
          <ClerkFailed>
            <ClerkFailedReport onShown={reportClerkFailed} />
            <FailureNotice mode={mode} publishableKey={publishableKey} />
          </ClerkFailed>
        )}
      <div ref={container} className="flex w-full justify-center">{children}</div>
    </div>
  );
};
